import groupBy from 'lodash/groupBy'
import { FC, ReactNode, useMemo, useState } from 'react'

import { iconByLeadSource } from '../../constants'
import { mainColors } from '../../styles'
import { CopyableContent } from '../CopyableContent'
import { TextField } from '../TextField'
import { XXSNormal } from '../Typography'
import * as S from './ExternalLinksModal.styles'
import { ICON_SIZE } from './ExternalLinksModal.styles'

type Icon = (typeof iconByLeadSource)['AVANTSTAY']['icon']

interface ExternalLink {
  url: string
  label: string
  groupId?: string
  icon?: Icon
  subtitle?: string
}

interface ExternalLinksModalProps {
  links: ExternalLink[]
  isLoading?: boolean
  isSearchable?: boolean
  modalTitle?: ReactNode
  onCloseModal?: VoidFunction
}

const checkIsLinkEnabled = (link: ExternalLink, search: string) => {
  if (search.trim() === '') return true
  return (link.label + link.url).toUpperCase().includes(search.toUpperCase())
}

function RenderIcon({ icon: Icon, iconSize = 24 }: { icon?: Icon; iconSize?: number }) {
  return Icon ? (
    <Icon width={iconSize} height={iconSize} style={{ width: iconSize, height: iconSize }} />
  ) : null
}

export const ExternalLinksModal: FC<ExternalLinksModalProps> = ({
  links,
  isLoading,
  isSearchable = true,
  modalTitle = 'Links',
  onCloseModal,
}) => {
  const [searchText, setSearchText] = useState('')

  const linksGroups = useMemo((): Array<{ title: string; links: ExternalLink[] }> => {
    const groupedLinks = groupBy(links, link => link.groupId || '')

    return Object.entries(groupedLinks).map(([groupId, links]) => ({
      title: groupId,
      links: (links as ExternalLink[]).map(({ url, label, icon, subtitle }) => ({
        url,
        label,
        icon,
        subtitle,
      })),
    }))
  }, [links])

  return (
    <S.StyledModal onCloseModal={onCloseModal}>
      <S.ModalTitle>{modalTitle}</S.ModalTitle>

      {isSearchable ? (
        <S.TextFieldWrapper>
          <S.IconSearchFilter />
          <TextField.Small
            placeholder="Search"
            disabled={isLoading}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
        </S.TextFieldWrapper>
      ) : null}

      {isLoading ? (
        <S.SectionLoading />
      ) : (
        linksGroups.map(({ title, links }) => (
          <S.Container key={title}>
            <S.SectionTitle>{title}</S.SectionTitle>
            {!links.length ? (
              <S.SectionEmpty>No links available</S.SectionEmpty>
            ) : (
              <S.GridCards>
                {links.map(i => (
                  <a
                    key={i.label + checkIsLinkEnabled(i, searchText)}
                    href={i.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <S.CardButton disabled={!checkIsLinkEnabled(i, searchText)}>
                      <S.LinkHeader>
                        <S.LinkIconFrame>
                          <RenderIcon icon={i.icon} iconSize={ICON_SIZE} />
                        </S.LinkIconFrame>

                        <S.CopyIconFrame
                          onClick={e => {
                            e.stopPropagation()
                            e.preventDefault()
                          }}
                        >
                          <CopyableContent
                            shouldHideValue
                            tooltipDescription={i.label}
                            valueToCopy={i.url}
                          />
                        </S.CopyIconFrame>
                      </S.LinkHeader>

                      {i.subtitle ? (
                        <XXSNormal color={mainColors.neutralExtremelyDark}>{i.subtitle}</XXSNormal>
                      ) : null}
                      <S.Text>{i.label}</S.Text>
                    </S.CardButton>
                  </a>
                ))}
              </S.GridCards>
            )}
          </S.Container>
        ))
      )}
    </S.StyledModal>
  )
}
